import { QueryClient } from "@tanstack/react-query";
import { UserEntitiesResponse } from "@common/types/entityContext";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity, EntityWithUser } from "@common/types/entity";
import { FinancialConnectionWithAccounts } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { addUnassignedAccountInQueryData } from "./unassignedAccounts";

export const entitiesAccountsQueryKey = "entities-accounts";

export function getEntitiesAccountsQueryKey(workspaceId: string) {
    return [entitiesAccountsQueryKey, workspaceId];
}

function clearErrorInFinancialAccount(
    account: FinancialAccount,
    connectionId: number,
): FinancialAccount {
    return {
        ...account,
        integrationAccounts: account.integrationAccounts?.map((ia) =>
            ia.connection?.id === connectionId
                ? {
                      ...ia,
                      connection: {
                          ...ia.connection,
                          error: null,
                      },
                  }
                : ia,
        ),
    };
}

function clearIntegrationErrorInFinancialAccount(
    account: FinancialAccount,
    connectionId: string,
): FinancialAccount {
    return {
        ...account,
        integrationAccounts: account.integrationAccounts?.map((ia) =>
            ia.integrationConnection?.id === connectionId
                ? {
                      ...ia,
                      integrationConnection: {
                          ...ia.integrationConnection,
                          error: null,
                      },
                  }
                : ia,
        ),
    };
}

export function clearConnectionErrorInQueryData(
    queryClient: QueryClient,
    connectionId: number,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                entities: data.entities.map((entity) => ({
                    ...entity,
                    financialAccounts: entity.financialAccounts.map((a) =>
                        clearErrorInFinancialAccount(a, connectionId),
                    ),
                })),
            };
        },
    );
}

export function clearIntegrationConnectionErrorInQueryData(
    queryClient: QueryClient,
    connectionId: string,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                entities: data.entities.map((entity) => ({
                    ...entity,
                    financialAccounts: entity.financialAccounts.map((a) =>
                        clearIntegrationErrorInFinancialAccount(
                            a,
                            connectionId,
                        ),
                    ),
                })),
            };
        },
    );
}

export function addFinancialConnectionInQueryData(
    queryClient: QueryClient,
    connection: FinancialConnectionWithAccounts,
) {
    connection.accounts.forEach((integrationAccount) => {
        if (!integrationAccount.financialAccount) {
            return;
        }

        const financialAccount = {
            ...integrationAccount.financialAccount,
            integrationAccounts: [{ ...integrationAccount, connection }],
        };

        if (financialAccount.entity) {
            addFinancialAccountInQueryData(
                queryClient,
                financialAccount,
                financialAccount.entity,
            );
        } else {
            addUnassignedAccountInQueryData(queryClient, financialAccount);
        }
    });
}

export function addIntegrationConnectionInQueryData(
    queryClient: QueryClient,
    connection: IntegrationConnection,
) {
    connection.integrationAccounts?.forEach((integrationAccount) => {
        if (!integrationAccount.financialAccount) {
            return;
        }

        const financialAccount = {
            ...integrationAccount.financialAccount,
            integrationAccounts: [
                { ...integrationAccount, integrationConnection: connection },
            ],
        };

        if (financialAccount.entity) {
            addFinancialAccountInQueryData(
                queryClient,
                financialAccount,
                financialAccount.entity,
            );
        } else {
            addUnassignedAccountInQueryData(queryClient, financialAccount);
        }
    });
}

function addFinancialAccountInQueryData(
    queryClient: QueryClient,
    account: FinancialAccount,
    entity: Entity,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                entities: data.entities.map((ec) =>
                    ec.entity.id === entity.id
                        ? {
                              ...ec,
                              financialAccounts: [
                                  ...ec.financialAccounts,
                                  account,
                              ],
                          }
                        : ec,
                ),
            };
        },
    );
}

export function updateFinancialAccountInQueryData(
    queryClient: QueryClient,
    account: FinancialAccount,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            // don't update to invalid state if received account without integrationAccounts
            if (!data || !account.integrationAccounts) {
                return data;
            }

            const previousAccountEntityContext = data.entities.find((ec) =>
                ec.financialAccounts.some((acc) => acc.id === account.id),
            );
            if (previousAccountEntityContext?.entity.id !== account.entityId) {
                return {
                    ...data,
                    entities: data.entities.map((ec) => {
                        if (
                            ec.entity.id ===
                            previousAccountEntityContext?.entity.id
                        ) {
                            return {
                                ...ec,
                                financialAccounts: ec.financialAccounts.filter(
                                    (acc) => acc.id !== account.id,
                                ),
                            };
                        }
                        if (ec.entity.id === account.entityId) {
                            return {
                                ...ec,
                                financialAccounts: [
                                    ...ec.financialAccounts,
                                    account,
                                ],
                            };
                        }
                        return ec;
                    }),
                };
            }

            return {
                ...data,
                entities: data.entities.map((ec) => ({
                    ...ec,
                    financialAccounts: ec.financialAccounts.map((acc) =>
                        acc.id === account.id ? account : acc,
                    ),
                })),
            };
        },
    );
}

export function removeFinancialAccountFromQueryData(
    queryClient: QueryClient,
    financialAccountId: number,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                entities: data.entities.map((ec) => ({
                    ...ec,
                    financialAccounts: ec.financialAccounts.filter(
                        (acc) => acc.id !== financialAccountId,
                    ),
                })),
            };
        },
    );
}

export function updateEntityInQueryData(
    queryClient: QueryClient,
    entity: Entity,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                entities: data.entities.map((ec) =>
                    ec.entity.id === entity.id
                        ? {
                              ...ec,
                              entity: {
                                  ...ec.entity,
                                  ...entity,
                              } as EntityWithUser,
                          }
                        : ec,
                ),
            };
        },
    );
}

export function updatePartialEntityInQueryData(
    queryClient: QueryClient,
    partialEntity: Partial<Entity>,
) {
    queryClient.setQueriesData<UserEntitiesResponse | undefined>(
        { queryKey: [entitiesAccountsQueryKey] },
        (data: UserEntitiesResponse | undefined) => {
            if (!data) {
                return data;
            }
            return {
                ...data,
                entities: data.entities.map((ec) =>
                    ec.entity.id === partialEntity.id
                        ? {
                              ...ec,
                              entity: {
                                  ...ec.entity,
                                  ...partialEntity,
                              } as EntityWithUser,
                          }
                        : ec,
                ),
            };
        },
    );
}
